import { Button, Spinner, Text } from '@fluentui/react-components';
import { AddRegular } from '@fluentui/react-icons';
import { computeTenantUsage } from '@worklogix/shared';
import type { TenantSummary } from '@worklogix/shared';
import { useEffect, useState } from 'react';

import { usePlatformApi } from '../api';
import type { PlatformApi } from '../api';
import { DataGrid } from '../components/DataGrid';
import type { ResourceColumn } from '../components/DataGrid';
import { TierBadge } from '../components/TierBadge';
import { UsageMeter } from '../components/UsageMeter';

import { toErrorMessage } from './errors';
import { formatDate } from './format';

/** Discriminated load state for the tenant resource list. */
type TenantsState =
  | { status: 'loading' }
  | { status: 'ready'; tenants: TenantSummary[] }
  | { status: 'error'; message: string };

/**
 * Fetch the platform tenant list, re-running whenever `refreshToken` changes
 * (bumped after a successful provision). Guards against setting state on an
 * unmounted / superseded request.
 */
function useTenants(api: PlatformApi, refreshToken: number): TenantsState {
  const [state, setState] = useState<TenantsState>({ status: 'loading' });

  useEffect(() => {
    let active = true;
    setState({ status: 'loading' });
    api
      .listTenants()
      .then((tenants) => {
        if (active) {
          setState({ status: 'ready', tenants });
        }
      })
      .catch((err: unknown) => {
        if (active) {
          setState({ status: 'error', message: toErrorMessage(err) });
        }
      });
    return () => {
      active = false;
    };
  }, [api, refreshToken]);

  return state;
}

/**
 * Resource-grid columns for the tenant list.
 *
 * The "Users" / "Usage" columns read `TenantSummary.userCount` and `.maxUsers`,
 * and "Tier" reads `.subscriptionTier` — all added to the shared contract by
 * Dev-A during this pass. `TierBadge` / `UsageMeter` are Dev-C components; usage
 * is computed via the shared `computeTenantUsage` rule (never re-implemented).
 */
const columns: ResourceColumn<TenantSummary>[] = [
  { columnId: 'name', header: 'Name', renderCell: (t) => t.name },
  { columnId: 'slug', header: 'Slug', renderCell: (t) => t.slug },
  { columnId: 'tier', header: 'Tier', renderCell: (t) => <TierBadge tier={t.subscriptionTier} /> },
  { columnId: 'created', header: 'Created', renderCell: (t) => formatDate(t.createdAt) },
  { columnId: 'users', header: 'Users', renderCell: (t) => t.userCount },
  {
    columnId: 'usage',
    header: 'Usage',
    renderCell: (t) => <UsageMeter usage={computeTenantUsage(t.userCount, t.maxUsers)} />,
  },
];

export interface TenantsListProps {
  /** Invoked when the command-bar "Create tenant" action is pressed. */
  onCreate: () => void;
  /** Invoked with a tenant id when a grid row is clicked (opens the detail blade). */
  onSelect: (id: string) => void;
  /** Monotonic token; changing it forces a reload of the list. */
  refreshToken: number;
}

function renderBody(state: TenantsState, onSelect: (id: string) => void): JSX.Element {
  if (state.status === 'loading') {
    return <Spinner label="Loading tenants…" />;
  }
  if (state.status === 'error') {
    return (
      <Text role="alert" className="wlx-list-error">
        {state.message}
      </Text>
    );
  }
  if (state.tenants.length === 0) {
    return (
      <Text className="wlx-list-empty">No tenants yet. Create the first one to get started.</Text>
    );
  }
  return (
    <DataGrid
      columns={columns}
      rows={state.tenants}
      getRowId={(t) => t.id}
      onRowClick={(t) => onSelect(t.id)}
    />
  );
}

/**
 * The Tenants resource-list page: an Azure-portal-style command bar with a
 * primary "Create tenant" action above a grid of tenants. Row click surfaces
 * the detail blade via {@link TenantsListProps.onSelect}.
 */
export function TenantsList({ onCreate, onSelect, refreshToken }: TenantsListProps): JSX.Element {
  const api = usePlatformApi();
  const state = useTenants(api, refreshToken);

  return (
    <section className="wlx-resource-list">
      <div className="wlx-command-bar">
        <Button appearance="primary" icon={<AddRegular />} onClick={onCreate}>
          Create tenant
        </Button>
      </div>
      {renderBody(state, onSelect)}
    </section>
  );
}
